import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"
import NumericInput from "src/main/PointOfSale/CurrentSalePanel/NumericInput"

import Button from "src/components/Button"
import LineItems from "src/components/LineItems/LineItems"
import NumberField from "src/components/NumberField"
import ToggleGroup from "src/components/ToggleGroup"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import {
  MIN_FUEL_GALLONS,
  fuelSelectionError,
  isPriced,
  lineTotalCents,
} from "./launchAddOnPricing"

// Only fuel items carry the unit fields, so neither `.isRequired` (warns on
// every non-fuel row) nor a plain optional type (silently allows a fuel row
// with no unit, which then throws on dereference) says what we mean.
const requiredForFuel =
  (type) =>
  (props, propName, componentName, ...rest) => {
    if (!props.fuel) return type(props, propName, componentName, ...rest)

    return type.isRequired(props, propName, componentName, ...rest)
  }

const LaunchAddOnFields = ({
  catalog,
  value,
  onChange,
  estimate = null,
  estimateFailed = false,
  validationAttempted = false,
}) => {
  const currency = estimate?.currency

  const selectionFor = (productId) =>
    value.find((selection) => selection.productId === productId)

  const removeProduct = (productId) => {
    onChange(value.filter((selection) => selection.productId !== productId))
  }

  const replaceSelection = (productId, selection) => {
    onChange([
      ...value.filter((existing) => existing.productId !== productId),
      selection,
    ])
  }

  const setStandardQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeProduct(productId)
      return
    }

    replaceSelection(productId, { productId, quantity })
  }

  const addFuelUnchosen = (productId) => {
    replaceSelection(productId, { productId, quantity: null, fuelMode: null })
  }

  const setFuelMode = (productId, fuelMode) => {
    const current = selectionFor(productId)
    replaceSelection(productId, {
      productId,
      quantity: fuelMode === "gallons" ? current?.quantity ?? null : null,
      fuelMode,
    })
  }

  const setFuelGallons = (productId, quantity) => {
    replaceSelection(productId, { productId, quantity, fuelMode: "gallons" })
  }

  // Figma's Dry Stack add-ons web spec calls for a clearly labeled "Remove"
  // control rather than mobile's icon-only close button, so a mouse user isn't
  // left guessing what a bare X does on a row with no swipe-to-delete gesture.
  // Blue, not the danger red — removing a fuel choice isn't a destructive
  // action here, it's just backing out of a selection.
  const renderRemoveButton = (item) => (
    <Button
      variant="ghost"
      small
      icon="fa-trash"
      iconStyle="solid"
      title={`Remove ${item.name}`}
      onClick={() => removeProduct(item.productId)}
    >
      Remove
    </Button>
  )

  // The other two states (out of stock, available) already read from their own
  // badge/Add button, so this is what tells a boater "yes, this one's in your
  // cart" — a fuel selection has no quantity count to do that job the way the
  // standard-item stepper below does.
  const renderAddedCheck = () => (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white"
    >
      <i className="fa-solid fa-check text-xs" />
    </span>
  )

  // An unorderable item keeps its row — a boater hunting for it should see why
  // it isn't on offer — but any selection made before it went unorderable has
  // to stay removable: isSubmittable drops it from the submit payload and
  // hasIncompleteAddOnSelection blocks the whole form over it, so the boater
  // needs a visible reason to click Remove rather than a submit button that
  // silently does nothing.
  const renderUnorderable = (item, reason) => {
    const selected = Boolean(selectionFor(item.productId))

    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <span className="rounded bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-900">
            {reason}
          </span>
          {selected && renderRemoveButton(item)}
        </div>
        {selected && validationAttempted && (
          <p className="text-xs font-semibold text-red-600">
            Remove this item to continue — it&apos;s no longer available.
          </p>
        )}
      </div>
    )
  }

  const renderStandardControl = (item, unorderable) => {
    if (unorderable) return renderUnorderable(item, unorderable)

    const selection = selectionFor(item.productId)

    if (!selection) {
      return (
        <Button
          variant="secondary"
          small
          title={`Add ${item.name}`}
          onClick={() => setStandardQuantity(item.productId, 1)}
        >
          Add
        </Button>
      )
    }

    // Decrementing from 1 removes the line entirely, so that button carries
    // the trash icon instead of a minus — same distinction the catalog's own
    // selection-button component draws between its 1-count and multi-count
    // states. onDecrementAtFloor is what makes the relabeled button actually
    // do that: without it, NumberField's own Decrement disables at min and
    // the click never reaches anything.
    return (
      <NumberField
        value={selection.quantity}
        min={1}
        onChange={(quantity) => setStandardQuantity(item.productId, quantity)}
        onDecrementAtFloor={() => removeProduct(item.productId)}
        ariaLabel={`Quantity of ${item.name}`}
        decrementLabel={
          selection.quantity === 1
            ? `Remove ${item.name}`
            : `Decrease quantity of ${item.name}`
        }
        decrementIcon={selection.quantity === 1 ? "fa-trash" : "fa-minus"}
        incrementLabel={`Increase quantity of ${item.name}`}
      />
    )
  }

  // Mirrors the product prototype's fuel-choice control exactly: adding a fuel
  // item leaves the mode unchosen (neither choice active). A boater must make a
  // real choice — assuming fill-up would silently decide the largest line on
  // the ticket.
  const renderFuelHint = (item, selection) => {
    // The server rejects an empty gallons line too, so it needs its own error;
    // the unchosen-mode message would be wrong (a mode *was* chosen) and the
    // gallons branch otherwise renders no hint at all.
    if (
      validationAttempted &&
      fuelSelectionError(item, selection) === "gallons"
    ) {
      return (
        <p className="text-xs font-semibold text-red-600">
          Enter how many {item.fuelUnit.name} to continue.
        </p>
      )
    }

    if (selection.fuelMode === "gallons") return null

    if (!selection.fuelMode) {
      if (validationAttempted) {
        return (
          <p className="text-xs font-semibold text-red-600">
            Choose fill-up or enter {item.fuelUnit.name} to continue.
          </p>
        )
      }
      return (
        <p className="text-xs text-yellow-700">
          How much fuel? {item.pricePerUnitFormatted} / {item.fuelUnit.symbol}{" "}
          today.
        </p>
      )
    }

    return (
      <p className="text-xs text-yellow-700">
        Fill-up quantity is finalized at checkout.
      </p>
    )
  }

  // Add-only state: no selection yet, so this sits on the same single row as
  // every other unselected catalog item. Once something's chosen, the fuel
  // row switches to the two-row layout in renderFuelSelectedSecondRow below —
  // the segmented control needs the row's full width, which a right-aligned
  // sibling of the item name can't give it.
  const renderFuelControl = (item, unorderable) => {
    if (unorderable) return renderUnorderable(item, unorderable)

    return (
      <Button
        variant="secondary"
        small
        title={`Add ${item.name}`}
        onClick={() => addFuelUnchosen(item.productId)}
      >
        Add
      </Button>
    )
  }

  const renderFuelSelectedTopRowControls = (item) => (
    <div className="flex items-center gap-2">
      {renderRemoveButton(item)}
      {renderAddedCheck()}
    </div>
  )

  // Full-width segmented control — matches the Desktop - Cart design
  // (node 5609:4411) rather than sizing to its own text.
  const renderFuelSegmentedControl = (item, selection, showError, error) => (
    <ToggleGroup
      ariaLabel={`Fuel choice for ${item.name}`}
      items={item.fuelModes}
      value={selection.fuelMode}
      onChange={(fuelMode) => setFuelMode(item.productId, fuelMode)}
      invalid={showError && error === "mode"}
    />
  )

  const renderFuelSelectedSecondRow = (item, selection) => {
    const error = fuelSelectionError(item, selection)
    const showError = validationAttempted && Boolean(error)

    return (
      <div className="flex flex-col gap-3">
        {renderFuelSegmentedControl(item, selection, showError, error)}
        {selection.fuelMode === "gallons" && (
          <div className="flex items-center gap-2">
            <div className="w-28">
              <NumericInput
                id={`fuel-gallons-${item.productId}`}
                ariaLabel={`Amount of ${item.name} in ${item.fuelUnit.name}`}
                value={selection.quantity}
                onChange={(quantity) =>
                  setFuelGallons(item.productId, quantity)
                }
                precision={5}
                minValue={MIN_FUEL_GALLONS}
                icon={item.fuelUnit.symbol}
                iconPosition="right"
                hasErrors={showError && error === "gallons"}
              />
            </div>
            {selection.quantity ? (
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrencyFromMinorUnits({
                  amount: lineTotalCents(item, selection.quantity),
                  currency,
                })}
              </span>
            ) : null}
          </div>
        )}
        {renderFuelHint(item, selection)}
      </div>
    )
  }

  const renderItem = (item) => {
    // Two things make an item unorderable: the marina is out of it, or it
    // carries no price for the server to snapshot onto the line. Which of the
    // two, and what to call it, is the server's to say — see the three
    // LaunchAddonProductSerializers.
    const unorderable = item.unavailableReason
    const unitPrice = isPriced(item) ? item.pricePerUnitFormatted : null
    const selection = selectionFor(item.productId)
    // A fuel selection needs its segmented control to span the row's full
    // width (Desktop - Cart, node 5609:4411), which means the row itself
    // stacks into two lines instead of sitting name-left/control-right like
    // every other row.
    const isFuelSelected = item.fuel && !unorderable && selection

    const nameBlock = (
      <div>
        <div className="text-base font-semibold" data-testid="addon-item-name">
          {item.name}
          {item.fuel && ` · ${item.fuelUnit.symbol}`}
        </div>
        {/* An item nobody can order says why in the price's place rather than
            advertising a price it won't honour (Add-on row Figma). */}
        {unorderable ? (
          <div className="text-muted text-sm">{unorderable}</div>
        ) : (
          unitPrice && (
            <div className="text-muted text-sm">
              {unitPrice}
              {item.fuel && ` / ${item.fuelUnit.symbol}`}
            </div>
          )
        )}
      </div>
    )

    return (
      <div
        key={item.productId}
        className={classNames(
          "rounded-lg px-3 py-2",
          isFuelSelected
            ? "flex flex-col gap-3"
            : "flex items-center justify-between",
          unorderable && "border border-gray-300 opacity-60",
          !unorderable &&
            (selection
              ? "border-2 border-blue-600 shadow-base"
              : "border border-gray-300")
        )}
      >
        {isFuelSelected ? (
          <>
            <div className="flex items-center justify-between">
              {nameBlock}
              {renderFuelSelectedTopRowControls(item)}
            </div>
            {renderFuelSelectedSecondRow(item, selection)}
          </>
        ) : (
          <>
            {nameBlock}
            {item.fuel
              ? renderFuelControl(item, unorderable)
              : renderStandardControl(item, unorderable)}
          </>
        )}
      </div>
    )
  }

  // Rendered, not decided. The order of the rows, their labels, which of them
  // are still partial, how each amount is formatted, and the disclaimer all
  // arrive from DryStack::LaunchAddonEstimateDecorator — the same object the
  // confirmation email is built from.
  const renderEstimate = () => {
    if (!estimateFailed && !estimate?.rows?.length) return null

    // The rows change under the boater as they edit quantities, so a reader who
    // isn't watching the block needs to be told the total moved.
    return (
      <div className="mt-4 border-t pt-3" aria-live="polite">
        {estimateFailed ? (
          // The last quote's rows are still in hand, but showing a total that
          // stopped tracking the cart is worse than showing none.
          <p className="text-xs font-semibold text-red-600">
            Couldn&apos;t total these up just now. Change a selection to try
            again — your confirmation will have the full estimate either way.
          </p>
        ) : (
          <>
            <LineItems
              lineItems={estimate.rows}
              currency={currency}
              spacing={1}
            />
            <p className="text-muted text-xs">{estimate.disclaimer}</p>
          </>
        )}
      </div>
    )
  }

  // Desktop - Cart groups the catalog under "Fuel" and "Other" subheaders
  // (nodes 5595:1701, 5595:1705) rather than one flat list — fuel first,
  // since that's the higher-stakes choice a boater has to make.
  const fuelItems = catalog.filter((item) => item.fuel)
  const otherItems = catalog.filter((item) => !item.fuel)

  const renderCategory = (label, items) =>
    items.length > 0 && (
      <React.Fragment key={label}>
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        {items.map(renderItem)}
      </React.Fragment>
    )

  return (
    <div>
      <div className="space-y-2">
        {renderCategory("Fuel", fuelItems)}
        {renderCategory("Other", otherItems)}
      </div>
      {renderEstimate()}
    </div>
  )
}

LaunchAddOnFields.propTypes = {
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      // Not read by this component — every branch here keys off `fuel`
      // instead. Left optional rather than dropped so callers with the raw
      // serializer shape on hand don't have to strip it out.
      productType: PropTypes.oneOf(["fuel", "standard", "fee"]),
      fuel: PropTypes.bool.isRequired,
      // Nullable: products.price_per_unit is, and the catalog query does not
      // filter on it. An unpriced item renders as unorderable.
      pricePerUnit: PropTypes.number,
      // Formatted by the server so fuel keeps its tenth of a cent and every
      // surface prints the same string. "-" when the product has no price.
      pricePerUnitFormatted: PropTypes.string,
      pricePrecision: PropTypes.oneOf(["cents", "hundredths_of_cents"])
        .isRequired,
      outOfStock: PropTypes.bool,
      // The server's words for why this item can't be ordered — "Out of stock",
      // "Unavailable" — shown in the price's place and in the row's badge. Null
      // when the item is orderable.
      unavailableReason: PropTypes.string,
      // Fuel only — the marina's unit and the server's wording for the two
      // choices, so a marina selling by the liter is never offered gallons.
      // Required on a fuel row: every fuel branch reads them unconditionally,
      // and a silent fallback would print gallons at a liters marina.
      fuelUnit: requiredForFuel(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          symbol: PropTypes.string.isRequired,
        })
      ),
      fuelModes: requiredForFuel(
        PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOf(["fill_up", "gallons"]).isRequired,
            label: PropTypes.string.isRequired,
          })
        )
      ),
    })
  ).isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string.isRequired,
      quantity: PropTypes.number,
      fuelMode: PropTypes.oneOf(["fill_up", "gallons", null]),
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  // The server's rendered Subtotal / Tax / Estimated total block. Null until the
  // first quote comes back, and while nothing priceable is selected.
  estimate: PropTypes.shape({
    // Handed straight to components/LineItems: `type` picks the component,
    // `label` and `suffix` are the server's words, `amount` is minor units.
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        // Raw minor units — LineItems re-formats this itself rather than
        // printing amountFormatted, so it can apply per-row currency.
        amount: PropTypes.number.isRequired,
        suffix: PropTypes.string,
      })
    ),
    // Needed alongside amount: LineItems formats the raw amount itself and
    // can't recover the marina's currency from an already-formatted string.
    currency: PropTypes.string,
    disclaimer: PropTypes.string,
  }),
  // The last quote failed. Distinct from a null estimate, which means there is
  // nothing priceable selected yet.
  estimateFailed: PropTypes.bool,
  validationAttempted: PropTypes.bool,
}

export default LaunchAddOnFields
